import {
  Component,
  OnInit,
  OnDestroy,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArchive,
  lucideCircleCheck,
  lucideCrop,
  lucideImagePlus,
  lucidePackage,
  lucidePlus,
  lucideTag,
  lucideTriangleAlert,
  lucideX,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmAlertDialogImports } from '@spartan-ng/helm/alert-dialog';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmCheckboxImports } from '@spartan-ng/helm/checkbox';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmEmptyImports } from '@spartan-ng/helm/empty';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmSwitchImports } from '@spartan-ng/helm/switch';
import { HlmTabsImports } from '@spartan-ng/helm/tabs';
import { HlmTextareaImports } from '@spartan-ng/helm/textarea';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { Router, ActivatedRoute } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Subject, takeUntil, filter } from 'rxjs';

import {
  VoucherProductService,
  VoucherProduct,
  VoucherProductCreate,
  VoucherProductUpdate,
  ImageCropData,
  VoucherProductVersion,
} from '../../services/voucher-product.service';
import { VoucherService } from '../../services/voucher.service';
import { LoggerService } from '../../services/logger.service';
import { CompanyService, Company } from '../../services/company.service';
import { ImageCropperModalComponent } from '../../components/image-cropper-modal/image-cropper-modal.component';
import { ProductCardComponent } from '../../components/product-card/product-card.component';
import { takeSelectedFile, validateImageFile } from '../../utils/image-file.utils';

// Custom validator for German price format
function priceValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }

  const priceString = control.value.toString().replace(',', '.');
  const price = parseFloat(priceString);

  if (isNaN(price) || price <= 0) {
    return { invalidPrice: true };
  }

  return null;
}

// Custom validator for description with line-based character counting.
// Each \n-separated segment occupies ceil(length / 60) rows of 60 chars.
// Max 10 rows total = 600 effective characters.
function descriptionValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  const effectiveCharCount = effectiveLength(control.value as string);

  if (effectiveCharCount > 600) {
    return {
      maxEffectiveLength: true,
      maxLength: 600,
      effectiveLength: effectiveCharCount,
    };
  }

  return null;
}

function effectiveLength(text: string): number {
  return text.split('\n').reduce((total: number, line: string) => {
    return total + Math.ceil(Math.max(line.length, 1) / 60) * 60;
  }, 0);
}

@Component({
  selector: 'app-products',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    FormsModule,
    NgIcon,
    HlmAlertImports,
    HlmAlertDialogImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmCheckboxImports,
    HlmDialogImports,
    HlmEmptyImports,
    HlmFieldImports,
    HlmInputImports,
    HlmItemImports,
    HlmSkeletonImports,
    HlmSpinnerImports,
    HlmSwitchImports,
    HlmTabsImports,
    HlmTextareaImports,
    HlmTypographyImports,
    ImageCropperModalComponent,
    ProductCardComponent,
  ],
  providers: [
    provideIcons({
      lucideArchive,
      lucideCircleCheck,
      lucideCrop,
      lucideImagePlus,
      lucidePackage,
      lucidePlus,
      lucideTag,
      lucideTriangleAlert,
      lucideX,
    }),
  ],
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProductsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  products: VoucherProduct[] = [];
  archivedProducts: VoucherProduct[] = [];
  productForm: FormGroup;
  showForm = false;
  editingProduct: VoucherProduct | null = null;
  selectedFile: File | null = null; // Original file
  selectedCroppedFile: File | null = null; // Cropped file for preview/upload
  selectedFileForCropping: File | null = null;
  previewUrl: string | null = null;
  imageToDelete = false; // Flag to mark image for deletion on save
  showCropperModal = false;
  cropData: ImageCropData | null = null;
  // Zoom/rotation to restore — only when re-cropping, never for a fresh pick.
  cropperStartData: ImageCropData | null = null;
  // Errors raised while the form dialog is open. `error` renders at the top of the
  // page, behind the modal, so a rejected photo (e.g. over 5 MB, common on phones)
  // or a failed save looked like nothing happened at all.
  imageError: string | null = null;
  formError: string | null = null;
  loading = false;
  downloadingExampleProductId: string | null = null;
  error: string | null = null;
  success: string | null = null;
  showArchived = false; // Toggle between active and archived view
  productsLive = false; // Whether products feature is live (voucher_products_enabled)
  togglingLive = false; // Loading state for the toggle
  moneyVoucherEnabled = true; // Whether money vouchers are enabled
  togglingMoneyVoucher = false; // Loading state for money voucher toggle
  companyPublicId: string | null = null; // Store company public_id for updates
  company: Company | null = null; // Store full company for widget

  /** A network pays its members the face value, so it sells nothing below it. */
  get givesDiscounts(): boolean {
    return this.company !== null && !this.company.is_group_admin;
  }

  // Version history
  showVersionHistory = false;
  versionHistoryProduct: VoucherProduct | null = null;
  versionHistory: VoucherProductVersion[] = [];
  loadingVersionHistory = false;

  // Toggle confirmation modal
  pendingToggleAction:
    | 'money-activate'
    | 'money-deactivate'
    | 'products-activate'
    | 'products-deactivate'
    | null = null;

  // Global discount controls
  showGlobalDiscountModal = false;
  globalDiscount = 0; // percentage to apply to all products (0-50)
  discountAcknowledged = false; // opt-in checkbox; must be re-checked each time
  applyingGlobalDiscount = false;
  globalDiscountError: string | null = null;
  globalDiscountSuccess: string | null = null;

  constructor(
    private fb: FormBuilder,
    public productService: VoucherProductService,
    private voucherService: VoucherService,
    private logger: LoggerService,
    private companyService: CompanyService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.productForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(70)]],
      description: ['', [descriptionValidator]],
      price: ['', [Validators.required, priceValidator]],
      discount_percentage: [0, [Validators.min(0), Validators.max(50)]],
      is_active: [true],
    });
  }

  ngOnInit(): void {
    // Subscribe to company changes to track productsLive status
    this.companyService.company$
      .pipe(
        takeUntil(this.destroy$),
        filter((company) => company !== null),
      )
      .subscribe((company) => {
        this.company = company;
        this.productsLive = company?.voucher_products_enabled || false;
        this.moneyVoucherEnabled =
          company?.money_voucher_enabled !== undefined ? company.money_voucher_enabled : true;
        this.companyPublicId = company?.public_id || null;

        // Auto-reload widget when company settings change
        this.reloadVoucherWidget();
      });

    this.loadProducts();
    this.loadArchivedProducts();

    this.route.fragment.pipe(takeUntil(this.destroy$)).subscribe((fragment) => {
      if (!fragment) return;
      setTimeout(() => {
        const el = document.getElementById(fragment);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('setting-highlight');
        setTimeout(() => {
          el.classList.remove('setting-highlight');
          el.classList.add('setting-highlight-fade');
          setTimeout(() => el.classList.remove('setting-highlight-fade'), 800);
        }, 2800);
      }, 350);
    });

    // Subscribe to products updates and auto-reload widget
    this.productService.products$.pipe(takeUntil(this.destroy$)).subscribe((products) => {
      this.products = products;

      // Auto-reload widget when products change
      this.reloadVoucherWidget();
    });

    // Subscribe to archived products updates
    this.productService.archivedProducts$.pipe(takeUntil(this.destroy$)).subscribe((products) => {
      this.archivedProducts = products;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** The resource owns loading and error — see VoucherProductService. */
  loadProducts(): void {
    this.productService.refresh();
  }

  loadArchivedProducts(): void {
    this.productService
      .loadArchivedProducts()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (error) => {
          this.logger.error('Error loading archived products:', error);
        },
      });
  }

  openForm(product?: VoucherProduct): void {
    this.showForm = true;
    this.editingProduct = product || null;
    this.error = null;
    this.success = null;
    this.imageError = null;
    this.formError = null;
    this.selectedFile = null;
    this.previewUrl = null;
    this.imageToDelete = false;

    if (product) {
      // Editing mode - populate form
      const priceInEuros = (product.price / 100).toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      this.productForm.patchValue({
        name: product.name,
        description: product.description || '',
        price: priceInEuros, // Convert cents to euros with German format
        discount_percentage: product.discount_percentage ?? 0,
        is_active: product.is_active,
      });

      // Set preview URL if product has image (with cache-busting)
      // Prefer cropped image for display, fallback to original
      const imagePath = product.image_cropped_path || product.image_path;
      if (imagePath) {
        this.previewUrl = this.productService.getImageUrl(imagePath, product.updated_at);
      }
    } else {
      // Creation mode - reset form
      this.productForm.reset({
        name: '',
        description: '',
        price: '',
        discount_percentage: 0,
        is_active: true,
      });
    }
  }

  closeForm(): void {
    this.showForm = false;
    this.editingProduct = null;
    this.selectedFile = null;
    this.selectedCroppedFile = null;
    this.selectedFileForCropping = null;
    this.previewUrl = null;
    this.imageToDelete = false;
    this.cropData = null;
    this.cropperStartData = null;
    this.imageError = null;
    this.formError = null;
    this.productForm.reset();
  }

  onFileSelected(event: Event): void {
    // The input is cleared here, not only on the error paths as before — it kept
    // its value after a successful pick, so choosing the same file again fired
    // no change event and nothing happened.
    const file = takeSelectedFile(event);
    if (!file) return;

    const problem = validateImageFile(file);
    if (problem) {
      this.imageError = problem;
      return;
    }

    this.imageError = null; // Clear any previous errors

    // Open cropper modal
    this.selectedFileForCropping = file;
    this.cropperStartData = null;
    this.showCropperModal = true;
  }

  onCropComplete(result: { file: File; croppedFile: File; cropData: ImageCropData }): void {
    this.selectedFile = result.file; // Original file
    this.selectedCroppedFile = result.croppedFile; // Cropped file
    this.cropData = result.cropData;
    this.showCropperModal = false;
    this.selectedFileForCropping = null;

    // Create preview URL from cropped file
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.previewUrl = e.target.result;
    };
    reader.readAsDataURL(result.croppedFile); // Show cropped version in preview
  }

  onCropCancel(): void {
    this.showCropperModal = false;
    this.selectedFileForCropping = null;
  }

  removeImage(): void {
    this.selectedFile = null;
    this.selectedCroppedFile = null;
    this.cropData = null;

    // Fall back to the current image; it stays unless marked for deletion.
    const imagePath = this.imageToDelete
      ? null
      : this.editingProduct?.image_cropped_path || this.editingProduct?.image_path;
    this.previewUrl = imagePath
      ? this.productService.getImageUrl(imagePath, this.editingProduct?.updated_at)
      : null;
  }

  markImageForDeletion(): void {
    // Mark image for deletion (will be deleted on save)
    this.imageToDelete = true;
    this.previewUrl = null;
    this.selectedFile = null;
  }

  editImage(): void {
    // Re-crop a photo picked in this session, not the server image.
    if (this.selectedFile) {
      this.selectedFileForCropping = this.selectedFile;
      this.cropperStartData = this.cropData;
      this.showCropperModal = true;
      return;
    }

    // Edit existing image - fetch original and open cropper
    if (!this.editingProduct || !this.editingProduct.image_path) {
      return;
    }

    this.loading = true;
    this.imageError = null;

    // Fetch the original image from the server
    const imageUrl = this.productService.getImageUrl(
      this.editingProduct.image_path,
      this.editingProduct.updated_at,
    );

    if (!imageUrl) {
      this.imageError = 'Bild-URL konnte nicht geladen werden.';
      this.loading = false;
      return;
    }

    // Convert image URL to File object
    fetch(imageUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        // Create a File object from the blob
        const file = new File([blob], `product-${this.editingProduct!.public_id}.jpg`, {
          type: blob.type || 'image/jpeg',
        });

        // Open cropper with the original image and existing crop data
        this.selectedFileForCropping = file;
        this.cropperStartData = this.editingProduct?.image_crop_data ?? null;
        this.showCropperModal = true;
        this.loading = false;
      })
      .catch((error) => {
        this.logger.error('Error loading image for editing:', error);
        this.imageError = 'Fehler beim Laden des Bildes. Bitte versuche es erneut.';
        this.loading = false;
      });
  }

  onSubmit(): void {
    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.error = null;
    this.success = null;
    this.formError = null;

    const formValue = this.productForm.value;
    // Handle both German (comma) and international (dot) decimal separators
    const priceString = formValue.price.toString().replace(',', '.');
    const priceInCents = this.productService.eurosToCents(parseFloat(priceString));
    const discountPercentage = this.givesDiscounts ? Number(formValue.discount_percentage) || 0 : 0;

    if (this.editingProduct) {
      // Update existing product
      const updateData: VoucherProductUpdate = {
        name: formValue.name,
        // Send an empty string (not null) so clearing the description is
        // persisted; the backend treats "" as "clear" and null as "unchanged".
        description: formValue.description ?? '',
        price: priceInCents,
        discount_percentage: discountPercentage,
        is_active: formValue.is_active,
      };

      this.productService
        .updateProduct(this.editingProduct.public_id, updateData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (product) => {
            // First handle image deletion if marked
            if (this.imageToDelete && this.editingProduct?.image_path) {
              this.deleteImageThenUpload(product.public_id);
            } else {
              this.handleImageUpload(product.public_id);
            }
          },
          error: (error) => {
            this.logger.error('Error updating product:', error);
            this.formError = 'Fehler beim Aktualisieren des Produkts';
            this.loading = false;
          },
        });
    } else {
      // Create new product
      const createData: VoucherProductCreate = {
        name: formValue.name,
        description: formValue.description || undefined,
        price: priceInCents,
        discount_percentage: discountPercentage,
        is_active: formValue.is_active,
      };

      this.productService
        .createProduct(createData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (product) => {
            this.handleImageUpload(product.public_id);
          },
          error: (error) => {
            this.logger.error('Error creating product:', error);
            this.formError = 'Fehler beim Erstellen des Produkts';
            this.loading = false;
          },
        });
    }
  }

  private deleteImageThenUpload(productId: string): void {
    // Delete existing image first
    this.productService
      .deleteProductImage(productId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // After deletion, upload new image if any
          this.handleImageUpload(productId);
        },
        error: (error) => {
          this.logger.error('Error deleting image:', error);
          // Continue with upload even if deletion fails
          this.handleImageUpload(productId);
        },
      });
  }

  private handleImageUpload(productId: string): void {
    if (this.selectedFile) {
      this.productService
        .uploadProductImage(
          productId,
          this.selectedFile, // Original file
          this.selectedCroppedFile || undefined, // Cropped file (if available)
          this.cropData || undefined, // Crop data
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.success = this.editingProduct
              ? 'Produkt erfolgreich aktualisiert'
              : 'Produkt erfolgreich erstellt';
            this.loading = false;
            this.closeForm();
          },
          error: (error) => {
            this.logger.error('Error uploading image:', error);
            this.error = 'Produkt gespeichert, aber Fehler beim Hochladen des Bildes';
            this.loading = false;
            this.closeForm();
          },
        });
    } else {
      this.success = this.editingProduct
        ? 'Produkt erfolgreich aktualisiert'
        : 'Produkt erfolgreich erstellt';
      this.loading = false;
      this.closeForm();
    }
  }

  archiveProduct(product: VoucherProduct): void {
    if (
      !confirm(
        `Möchtest du das Produkt "${product.name}" wirklich archivieren? Es wird ins Archiv verschoben.`,
      )
    ) {
      return;
    }

    this.loading = true;
    this.productService
      .archiveProduct(product.public_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.success = 'Produkt erfolgreich archiviert';
          this.loading = false;
        },
        error: (error) => {
          this.logger.error('Error archiving product:', error);
          this.error = 'Fehler beim Archivieren des Produkts';
          this.loading = false;
        },
      });
  }

  unarchiveProduct(product: VoucherProduct): void {
    this.loading = true;
    this.productService
      .unarchiveProduct(product.public_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.success = 'Produkt erfolgreich wiederhergestellt';
          this.loading = false;
        },
        error: (error) => {
          this.logger.error('Error unarchiving product:', error);
          this.error = 'Fehler beim Wiederherstellen des Produkts';
          this.loading = false;
        },
      });
  }

  toggleProductStatus(product: VoucherProduct): void {
    const updateData: VoucherProductUpdate = {
      is_active: !product.is_active,
    };

    this.loading = true;
    this.productService
      .updateProduct(product.public_id, updateData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.success = product.is_active ? 'Produkt deaktiviert' : 'Produkt aktiviert';
          this.loading = false;
        },
        error: (error) => {
          this.logger.error('Error toggling product status:', error);
          this.error = 'Fehler beim Ändern des Status';
          this.loading = false;
        },
      });
  }

  downloadProductExample(product: VoucherProduct): void {
    this.downloadingExampleProductId = product.public_id;
    this.voucherService
      .downloadProductExamplePDF(product.public_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `beispiel_${product.name.toLowerCase().replace(/\s+/g, '_')}_gutschein.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.downloadingExampleProductId = null;
        },
        error: (error) => {
          this.logger.error('Error downloading product example PDF:', error);
          this.error = 'Fehler beim Herunterladen des Beispiel-Gutscheins';
          this.downloadingExampleProductId = null;
        },
      });
  }

  getImageUrl(imagePath: string | null, updatedAt?: string): string | null {
    return this.productService.getImageUrl(imagePath, updatedAt);
  }

  formatPrice(priceInCents: number): string {
    return this.productService.formatPrice(priceInCents);
  }

  openGlobalDiscountModal(): void {
    // Fresh opt-in each time the modal is opened
    this.discountAcknowledged = false;
    this.globalDiscountError = null;
    this.globalDiscountSuccess = null;
    this.showGlobalDiscountModal = true;
  }

  closeGlobalDiscountModal(): void {
    if (this.applyingGlobalDiscount) {
      return;
    }
    this.showGlobalDiscountModal = false;
  }

  /** Whether the "apply to all" action may run. */
  get canApplyGlobalDiscount(): boolean {
    return (
      this.discountAcknowledged &&
      this.globalDiscount >= 1 &&
      this.globalDiscount <= 50 &&
      !this.applyingGlobalDiscount &&
      this.products.length > 0
    );
  }

  /** Apply the chosen discount to every product. Requires the opt-in checkbox. */
  applyGlobalDiscount(): void {
    if (!this.canApplyGlobalDiscount) {
      return;
    }
    this.applyingGlobalDiscount = true;
    this.globalDiscountError = null;
    this.globalDiscountSuccess = null;

    this.productService
      .setDiscountForAll(Math.round(this.globalDiscount))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.applyingGlobalDiscount = false;
          this.globalDiscountSuccess = `${this.globalDiscount}% Rabatt auf ${res.updated_count} Produkt(e) angewendet.`;
          // Require a fresh opt-in for the next change
          this.discountAcknowledged = false;
        },
        error: (err) => {
          this.applyingGlobalDiscount = false;
          this.logger.error('Error applying global discount:', err);
          this.globalDiscountError = 'Der Rabatt konnte nicht angewendet werden.';
        },
      });
  }

  /** Reset every product's discount to 0. No opt-in required (removes discounts). */
  removeAllDiscounts(): void {
    if (this.applyingGlobalDiscount) {
      return;
    }
    this.applyingGlobalDiscount = true;
    this.globalDiscountError = null;
    this.globalDiscountSuccess = null;

    this.productService
      .setDiscountForAll(0)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.applyingGlobalDiscount = false;
          this.globalDiscount = 0;
          this.discountAcknowledged = false;
          this.globalDiscountSuccess = `Rabatte auf ${res.updated_count} Produkt(e) entfernt.`;
        },
        error: (err) => {
          this.applyingGlobalDiscount = false;
          this.logger.error('Error removing discounts:', err);
          this.globalDiscountError = 'Die Rabatte konnten nicht entfernt werden.';
        },
      });
  }

  /**
   * Price (in cents) the customer pays given the current form's price and
   * discount, or null when there is no valid price. Rounds half-to-even to
   * match the backend's Postgres CAST(... AS INTEGER).
   */
  discountedPricePreview(): number | null {
    const rawPrice = this.productForm.get('price')?.value;
    if (!rawPrice) {
      return null;
    }
    const priceInCents = this.productService.eurosToCents(
      parseFloat(rawPrice.toString().replace(',', '.')),
    );
    if (isNaN(priceInCents)) {
      return null;
    }
    const discount = Number(this.productForm.get('discount_percentage')?.value) || 0;
    if (discount <= 0) {
      return priceInCents;
    }
    return Math.round((priceInCents * (100 - discount)) / 100);
  }

  getEffectiveCharCount(): number {
    const description = this.productForm.get('description')?.value || '';
    if (!description) return 0;
    return effectiveLength(description);
  }

  getLineCount(): number {
    const description = this.productForm.get('description')?.value || '';
    if (!description) return 0;
    return description.split('\n').length;
  }

  /** A control shows its error only once the user has touched it. */
  isInvalid(controlName: string): boolean {
    const control = this.productForm.get(controlName);
    return !!control?.invalid && !!control?.touched;
  }

  onFormDialogState(state: 'open' | 'closed'): void {
    if (state === 'closed' && this.showForm && !this.loading) {
      this.closeForm();
    }
  }

  onVersionHistoryDialogState(state: 'open' | 'closed'): void {
    if (state === 'closed' && this.showVersionHistory) {
      this.closeVersionHistory();
    }
  }

  onToggleDialogState(state: 'open' | 'closed'): void {
    if (state === 'closed' && this.pendingToggleAction) {
      this.cancelToggleConfirm();
    }
  }

  onDiscountDialogState(state: 'open' | 'closed'): void {
    if (state === 'closed' && this.showGlobalDiscountModal) {
      this.closeGlobalDiscountModal();
    }
  }

  dismissMessage(): void {
    this.error = null;
    this.success = null;
  }

  toggleProductsLive(newStatus: boolean): void {
    if (this.togglingLive) return;

    if (!this.companyPublicId) {
      this.error = 'Fehler: Keine Firma gefunden. Bitte lade die Seite neu.';
      this.productsLive = !newStatus;
      return;
    }

    // Validate that at least one voucher type will be enabled
    if (!newStatus && !this.moneyVoucherEnabled && !this.company?.fixed_voucher_amounts_enabled) {
      this.error =
        'Mindestens eine Gutschein-Art (Wert-Gutscheine oder Produkt-Gutscheine) muss aktiviert sein.';
      this.productsLive = !newStatus;
      return;
    }

    // Ask for confirmation in the modal before toggling
    this.pendingToggleAction = newStatus ? 'products-activate' : 'products-deactivate';
  }

  cancelToggleConfirm(): void {
    // The toggle already flipped; revert it since the action was cancelled
    switch (this.pendingToggleAction) {
      case 'money-activate':
        this.moneyVoucherEnabled = false;
        break;
      case 'money-deactivate':
        this.moneyVoucherEnabled = true;
        break;
      case 'products-activate':
        this.productsLive = false;
        break;
      case 'products-deactivate':
        this.productsLive = true;
        break;
    }
    this.pendingToggleAction = null;
  }

  confirmToggle(): void {
    const action = this.pendingToggleAction;
    this.pendingToggleAction = null;

    switch (action) {
      case 'money-activate':
        this.performMoneyVoucherToggle(true);
        break;
      case 'money-deactivate':
        this.performMoneyVoucherToggle(false);
        break;
      case 'products-activate':
        this.performProductsLiveToggle(true);
        break;
      case 'products-deactivate':
        this.performProductsLiveToggle(false);
        break;
    }
  }

  get toggleConfirmTitle(): string {
    switch (this.pendingToggleAction) {
      case 'money-activate':
        return 'Wert-Gutscheine aktivieren?';
      case 'money-deactivate':
        return 'Wert-Gutscheine deaktivieren?';
      case 'products-activate':
        return 'Wichtige Information zu Produkt-Gutscheinen';
      case 'products-deactivate':
        return 'Produkt-Gutscheine deaktivieren?';
      default:
        return '';
    }
  }

  get toggleConfirmSubtitle(): string {
    return this.pendingToggleAction === 'products-activate' ? 'Steuerrechtliche Anforderungen' : '';
  }

  get toggleConfirmMessage(): string {
    switch (this.pendingToggleAction) {
      case 'money-activate':
        return 'Deine Kunden können dann Gutscheine mit frei wählbarem Betrag kaufen.';
      case 'money-deactivate':
        return 'Deine Kunden können dann nur noch Produkt-Gutscheine kaufen.';
      case 'products-deactivate':
        return 'Deine Kunden sehen dann wieder nur den Standard-Wertgutschein.';
      default:
        return '';
    }
  }

  get toggleConfirmButtonText(): string {
    switch (this.pendingToggleAction) {
      case 'money-activate':
        return 'Wert-Gutscheine aktivieren';
      case 'money-deactivate':
        return 'Wert-Gutscheine deaktivieren';
      case 'products-activate':
        return 'Verstanden, Produkt-Gutscheine aktivieren';
      case 'products-deactivate':
        return 'Produkt-Gutscheine deaktivieren';
      default:
        return '';
    }
  }

  get toggleConfirmIsDeactivation(): boolean {
    return (
      this.pendingToggleAction === 'money-deactivate' ||
      this.pendingToggleAction === 'products-deactivate'
    );
  }

  private performProductsLiveToggle(newStatus: boolean): void {
    this.togglingLive = true;
    this.error = null;
    this.success = null;

    this.companyService
      .updateCompanyDetails({
        public_id: this.companyPublicId!,
        voucher_products_enabled: newStatus,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.productsLive = newStatus;
          this.success = newStatus
            ? 'Produkt-Gutscheine sind jetzt live! 🎉'
            : 'Produkt-Gutscheine wurden deaktiviert. Kunden sehen wieder den Standard-Wertgutschein.';
          this.togglingLive = false;

          // Auto-dismiss success message after 5 seconds
          setTimeout(() => {
            this.success = null;
          }, 5000);
        },
        error: (error) => {
          const errorMessage =
            error.error?.detail || 'Fehler beim Ändern des Status. Bitte versuche es erneut.';
          this.logger.error('Error toggling products live status:', error);
          this.error = errorMessage;
          this.productsLive = !newStatus;
          this.togglingLive = false;
        },
      });
  }

  toggleMoneyVoucher(newStatus: boolean): void {
    if (this.togglingMoneyVoucher) return;

    if (!this.companyPublicId) {
      this.error = 'Fehler: Keine Firma gefunden. Bitte lade die Seite neu.';
      this.moneyVoucherEnabled = !newStatus;
      return;
    }

    // Validate that at least one voucher type will be enabled
    if (!newStatus && !this.productsLive && !this.company?.fixed_voucher_amounts_enabled) {
      this.error =
        'Mindestens eine Gutschein-Art (Wert-Gutscheine oder Produkt-Gutscheine) muss aktiviert sein.';
      this.moneyVoucherEnabled = !newStatus;
      return;
    }

    // Ask for confirmation in the modal before toggling
    this.pendingToggleAction = newStatus ? 'money-activate' : 'money-deactivate';
  }

  private performMoneyVoucherToggle(newStatus: boolean): void {
    this.togglingMoneyVoucher = true;
    this.error = null;
    this.success = null;

    this.companyService
      .updateCompanyDetails({
        public_id: this.companyPublicId!,
        money_voucher_enabled: newStatus,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.moneyVoucherEnabled = newStatus;
          this.success = newStatus
            ? 'Wert-Gutscheine sind jetzt aktiviert! 🎉'
            : 'Wert-Gutscheine wurden deaktiviert.';
          this.togglingMoneyVoucher = false;

          // Auto-dismiss success message after 5 seconds
          setTimeout(() => {
            this.success = null;
          }, 5000);
        },
        error: (error) => {
          const errorMessage =
            error.error?.detail || 'Fehler beim Ändern des Status. Bitte versuche es erneut.';
          this.logger.error('Error toggling money voucher status:', error);
          this.error = errorMessage;
          this.moneyVoucherEnabled = !newStatus;
          this.togglingMoneyVoucher = false;
        },
      });
  }

  moveProductLeft(product: VoucherProduct): void {
    const currentIndex = this.products.findIndex((p) => p.public_id === product.public_id);
    if (currentIndex <= 0) return; // Already at the start

    // Add animation class
    this.addMovingAnimation(product.public_id);

    // Swap with previous item
    const newIndex = currentIndex - 1;
    [this.products[currentIndex], this.products[newIndex]] = [
      this.products[newIndex],
      this.products[currentIndex],
    ];

    // Add highlight to both affected cards
    this.addPositionChangedAnimation(this.products[currentIndex].public_id);
    this.addPositionChangedAnimation(this.products[newIndex].public_id);

    this.saveProductOrder();
  }

  moveProductRight(product: VoucherProduct): void {
    const currentIndex = this.products.findIndex((p) => p.public_id === product.public_id);
    if (currentIndex >= this.products.length - 1) return; // Already at the end

    // Add animation class
    this.addMovingAnimation(product.public_id);

    // Swap with next item
    const newIndex = currentIndex + 1;
    [this.products[currentIndex], this.products[newIndex]] = [
      this.products[newIndex],
      this.products[currentIndex],
    ];

    // Add highlight to both affected cards
    this.addPositionChangedAnimation(this.products[currentIndex].public_id);
    this.addPositionChangedAnimation(this.products[newIndex].public_id);

    this.saveProductOrder();
  }

  private addMovingAnimation(productId: string): void {
    const element = document.querySelector(`app-product-card[data-product-id="${productId}"]`);
    if (element) {
      element.classList.add('moving');
      setTimeout(() => {
        element.classList.remove('moving');
      }, 400);
    }
  }

  private addPositionChangedAnimation(productId: string): void {
    const element = document.querySelector(`app-product-card[data-product-id="${productId}"]`);
    if (element) {
      element.classList.add('position-changed');
      setTimeout(() => {
        element.classList.remove('position-changed');
      }, 600);
    }
  }

  private saveProductOrder(): void {
    // Prepare order data
    const productOrders = this.products.map((product, index) => ({
      public_id: product.public_id,
      display_order: index,
    }));

    // Send to backend
    this.productService
      .reorderProducts(productOrders)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Success - products are already reordered locally
          this.success = 'Reihenfolge gespeichert';

          setTimeout(() => {
            this.success = null;
          }, 1500);
        },
        error: (error) => {
          this.logger.error('Error reordering products:', error);
          this.error = 'Fehler beim Sortieren der Produkte';
          // Reload to restore correct order
          this.loadProducts();
          setTimeout(() => {
            this.error = null;
          }, 3000);
        },
      });
  }

  /**
   * Reload the voucher widget to reflect changes
   */
  private reloadVoucherWidget(): void {
    // Dispatch custom event to reload all voucher widgets
    const voucherWidgets = document.querySelectorAll('schenkwerk-voucher');
    voucherWidgets.forEach((widget) => {
      widget.dispatchEvent(new CustomEvent('reload-widget'));
    });
  }

  /**
   * Open version history modal for a product
   */
  openVersionHistory(product: VoucherProduct): void {
    this.versionHistoryProduct = product;
    this.showVersionHistory = true;
    this.loadingVersionHistory = true;
    this.versionHistory = [];

    this.productService
      .getVersionHistory(product.public_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (versions) => {
          this.versionHistory = versions;
          this.loadingVersionHistory = false;
        },
        error: (error) => {
          this.logger.error('Error loading version history:', error);
          this.error = 'Fehler beim Laden der Versionshistorie';
          this.loadingVersionHistory = false;
          setTimeout(() => {
            this.error = null;
          }, 3000);
        },
      });
  }

  /**
   * Close version history modal
   */
  closeVersionHistory(): void {
    this.showVersionHistory = false;
    this.versionHistoryProduct = null;
    this.versionHistory = [];
  }

  /**
   * Format version number for display
   */
  formatVersionNumber(version: VoucherProductVersion): string {
    return `v${version.version_number}`;
  }

  /**
   * Get badge class for version
   */
  getVersionBadgeClass(version: VoucherProductVersion): string {
    return version.is_current ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600';
  }

  /**
   * Get store ID for voucher widget
   */
  getStoreId(): string | undefined {
    return this.company?.public_id;
  }
}
